import { useEffect, useState } from "react";
import { kebabCase } from 'lodash';
import { usePermissions, useTranslate } from "../../customHooks";
import { makeStyles } from '@material-ui/core/styles';
import { PANTitle, toast } from "../../components";
import { withRouter } from "react-router-dom";
import { dataProvider } from "../../dataProvider";
import * as DataTypes from "../../api/FwaasDataTypes";
import { PANWDSTable, PANWDSTooltip } from "../../components/PANWDSElements";
import _ from "lodash";
import { getUsageFilters } from "./utils/usageFilters";
import { getMonthsInCSTFormat, getDateFormat, getPreviousDay } from "./utils/generateDateUtils";
import { Link } from "@panwds/react-ui";

const useStyles = makeStyles((theme) => ({
  appColumnStyles: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '100px'
  },
  checkIcon: {
    color: "#33CCB8",
  },
}));

export const UsageExplorer = (props: any) => {
  const translate = useTranslate();
  const classes = useStyles();
  const { permissions } = usePermissions();
  let startDate = getMonthsInCSTFormat(new Date(), -6);
  let endDate = getPreviousDay(new Date());
  const [usageData, setUsageData] = useState<any>([]);
  const [loadingCreditUsage, setLoadingCreditUsage] = useState<boolean>(true);
  const [usageFilters, setUsageFilters] = useState<any>(getUsageFilters([]));
  const [purchasedCredits, setPurchasedCredits] = useState(0);
  const [freeTrial, setFreeTrial] = useState(false);

  const processBillingInfo = (billingInfo: any[]) => {
    let freeTrial = false;
    let saasContractActive = false;
    let paygActive = false;
    let totalCredit = 0;

    // if we have saas contract active and a valid payg
    billingInfo.map((info: any) => {
      if (info.Type === "FreeTrial") {
        freeTrial = info.Status === "Active";
        if (freeTrial) {
          setFreeTrial(true);
          totalCredit = info.Credit;
        }
      }
      if (info.Type === "Contract") {
        saasContractActive = info.Status === "Active";
        if (saasContractActive) {
          totalCredit = info.Credit;
        }
      }
      if (info.Type === "PAYG") {
        paygActive = info.Status === "Active";
        if (paygActive) {
          totalCredit = info.Credit;
        }
      }
    });
    return { freeTrial, saasContractActive, totalCredit }
  }

  useEffect(() => {
    dataProvider.describe("billing", '', { startDate: startDate, endDate: endDate })
      .then(async (response: DataTypes.IFwaasApiResponse) => {
        if (response.data) {
          let activeBillingType = processBillingInfo(response.data.BillingInfo);
          let totalPurchasedCredits = activeBillingType.totalCredit;
          let usageData = [];
          if (activeBillingType.saasContractActive || activeBillingType.freeTrial) {
            usageData = response.data.CreditUsageInfo;
          } else {
            usageData = response.data.CreditUsageInfo.concat(response.data.UnitUsageInfo);
          }
          usageData = usageData.map(({ TotalCredit, ...data }: any) => ({
            ...data,
            UsedCredit: data.UsedCredit && data.UsedCredit > 0 ? data.UsedCredit : '-',
            UsedUnit: data.UsedCredit && data.UsedCredit > 0 ? '-' : data.UsedUnit ? data.UsedUnit : '-'
          }));

          // Function to zero-pad single digits for months and days
          const zeroPad = (num: any) => String(num).padStart(2, '0');

          // Function to convert date strings to a sortable format (YYYY-MM-DD)
          const toSortableDate = (dateStr: any) => {
            const parts = dateStr.split('-');
            return `${parts[0]}-${zeroPad(parts[1])}-${zeroPad(parts[2])}`;
          };

          // Sort the data by date
          usageData.sort((a: any, b: any) => {
            const dateA = toSortableDate(a.Date);
            const dateB = toSortableDate(b.Date);
            return dateB.localeCompare(dateA);
          });

          // Set the usage data state with the preprocessed array
          setUsageData(usageData);
          setPurchasedCredits(totalPurchasedCredits);
          setUsageFilters(getUsageFilters([...new Set(usageData?.map((ele: any) => ele.Dimension))]))
        }
        else {
          setUsageData([]);
          toast.error(response?.error, { toastId: `usage-history-error` });
        }
      })
      .catch((error: any) => {
        if (error?.noToast) {
          return;
        }
        setUsageData([]);
        toast.error(error?.error, { toastId: `usage-history-error` });
      }).finally(() => {
        setLoadingCreditUsage(false);
      });
    return;
  }, []);

  const loadDateFilter = (dateRange: any) => {
    let clonedUsageData = _.clone(usageData);
    let startDate = '';
    let endDate = '';
    if (dateRange === '1') {
      startDate = getMonthsInCSTFormat(new Date(), -1);
      endDate = getPreviousDay(new Date());
    } else if (dateRange === '3') {
      startDate = getMonthsInCSTFormat(new Date(), -3);
      endDate = getPreviousDay(new Date());
    } else if (dateRange === '6') {
      startDate = getMonthsInCSTFormat(new Date(), -6);
      endDate = getPreviousDay(new Date());
    }

    let filteredData = clonedUsageData.filter((data: any) => {
      if (!data?.Date) return false;

      try {
        // Convert dates to ISO string format first, then parse
        const dataDateStr = data.Date.toString();
        const dataDate = new Date(dataDateStr.replace(/-/g, '/'));  // Safari prefers MM/DD/YYYY or YYYY/MM/DD
        const startDateTime = new Date(startDate.toString().replace(/-/g, '/'));
        const endDateTime = new Date(endDate.toString().replace(/-/g, '/'));

        // Additional validation
        if (isNaN(dataDate.getTime()) || isNaN(startDateTime.getTime()) || isNaN(endDateTime.getTime())) {
          return false;
        }

        return dataDate.getTime() >= startDateTime.getTime() && dataDate.getTime() <= endDateTime.getTime();
      } catch (error) {
        return false;
      }
    });

    // Function to zero-pad single digits for months and days
    const zeroPad = (num: number) => String(num).padStart(2, '0');

    // Function to convert date strings to a sortable format (YYYY-MM-DD)
    const toSortableDate = (dateStr: string) => {
      const parts = dateStr.split('-');
      return `${parts[0]}-${zeroPad(parseInt(parts[1]))}-${zeroPad(parseInt(parts[2]))}`;
    };

    // Sort the filtered data by the Date field
    filteredData.sort((a: any, b: any) => {
      const dateA = toSortableDate(a.Date);
      const dateB = toSortableDate(b.Date);
      return dateB.localeCompare(dateA);
    });

    return filteredData;
  }

  const getUsageColumns = () => [
    {
      accessor: 'Date',
      Header: translate(`resources.usage.period`),
      columnSize: 3,
      render: ({ row }: any) => {
        return (
          <span
            key={`usage-history-record-${kebabCase(row?.original?.Date?.toLowerCase())}`}
            className={classes.appColumnStyles}
            data-test-id={`usage-history-record-${kebabCase(row?.original?.Date?.toLowerCase())}`}
          >
            {getDateFormat(row?.original?.Date)}
          </span>
        )
      }
    },
    {
      accessor: 'Dimension',
      noTooltip: true,
      Header: () => {
        return (
          <PANWDSTooltip showTooltip={true} label={translate(`resources.usage.dimensionTooltip`)}>
            <span>
              {translate(`resources.usage.dimension`)}
            </span>
          </PANWDSTooltip>
        )
      },
      columnSize: 3,
      render: ({ row }: any) => {
        return (
          <span
            key={`usage-history-record-${kebabCase(row?.original?.Dimension?.toLowerCase())}`}
            className={classes.appColumnStyles}
            data-test-id={`usage-history-record-${kebabCase(row?.original?.Dimension?.toLowerCase())}`}
          >
            {row?.original?.Dimension}
          </span>
        )
      }
    },
    {
      accessor: 'UsedUnit',
      columnSize: 3,
      noTooltip: true,
      Header: function HeaderComponent() {
        return (
          <PANWDSTooltip showTooltip={true} label={translate(`resources.usage.consumedUnitsTooltip`)}>
            <span className="tw-flex tw-gap-1 tw-justify-between tw-items-center">
              <Link
                dataMetrics="cloudngfw-overviewScreen-getting-card-onClickOfRulesAndObjects"
                external
                href="https://docs.paloaltonetworks.com/cloud-ngfw/aws/cloud-ngfw-on-aws/getting-started-with-cloud-ngfw-for-aws/pricing"
              >
                <span>{translate(`resources.usage.consumedUnits`)}</span>
              </Link>
            </span>
          </PANWDSTooltip>
        )
      },
      render: ({ row }: any) => {
        return (
          <span
            key={`usage-history-record-${kebabCase(row?.original?.UsedUnit)}`}
            className={classes.appColumnStyles}
            data-test-id={`usage-history-record-${kebabCase(row?.original?.UsedUnit)}`}
          >
            {row?.original?.UsedCredit && row?.original?.UsedCredit > 0 ? '-' : row?.original?.UsedUnit ? row?.original?.UsedUnit : '-'}
          </span>
        )
      }
    },
    {
      accessor: 'UsedCredit',
      Header: () => {
        return (
          <PANWDSTooltip showTooltip={true} label={translate(`resources.usage.consumedCreditsTooltip`)}>
            <span>
              {translate(`resources.usage.consumedCredits`)}
            </span>
          </PANWDSTooltip>
        )
      },
      columnSize: 3,
      noTooltip: true,
      render: ({ row }: any) => {
        return (
          <span
            key={`usage-history-record-${kebabCase(row?.original?.UsedCredit)}`}
            className={classes.appColumnStyles}
            data-test-id={`usage-history-record-${kebabCase(row?.original?.UsedCredit)}`}
          >
            {row?.original?.UsedCredit > 0 ? row?.original?.UsedCredit : '-'}
          </span>
        )
      }
    },
    //bring back post 2.2.1
    // {
    //   accessor: 'TotalCredit',
    //   noTooltip: true,
    //   Header: () => {
    //       return (
    //           <PANWDSTooltip showTooltip={true} label={translate(`resources.usage.purchasedCreditsTooltip`)}>
    //               <span>
    //                   {translate(`resources.usage.purchasedCredits`)}
    //               </span>
    //           </PANWDSTooltip>
    //       )
    //   },
    //   columnSize: 2,
    //   render: ({ row }: any) => {
    //     return (
    //       <span
    //         key={`usage-history-record-${kebabCase(row?.original?.TotalCredit?.toLowerCase())}`}
    //         className={classes.appColumnStyles}
    //         data-test-id={`usage-history-record-${kebabCase(row?.original?.TotalCredit?.toLowerCase())}`}
    //       >
    //         {row?.original?.TotalCredit}
    //       </span>
    //     )
    //   }
    // }
  ];

  //bring back post 2.2.1
  const usageToolBarActions: any[] = [];
  usageToolBarActions.push({
    title: translate(`resources.usage.downloadAsCsv`),
    dataMetrics: "cloudngfw-users-create-btn",
    type: 'downloadCsv',
    disabled: usageData?.length === 0,
    fileName: "usage-history.csv"
  });

  return (
    <>
      <PANTitle title={translate("resources.usage.name")} tag={translate("resources.usage.beta")} divider={true} paddingContainer="16px" />
      <PANWDSTable
        permissions={permissions}
        columns={getUsageColumns()}
        gridData={usageData}
        loading={loadingCreditUsage}
        title={translate(`resources.usage.usageHistory`)}
        subtitleInfo={loadingCreditUsage ? undefined : freeTrial ? `${translate(`resources.usage.totalCredits`) + ': ' + purchasedCredits}` : `${translate(`resources.usage.purchasedCredits`) + ': ' + purchasedCredits}`}
        searchFilterRequired={true}
        showToolbar={true}
        idProperty={"usage-history"}
        showTileTitle
        emptySubtitle={translate(`resources.usage.noUsageHistory`)}
        singleSelect={false}
        multiSelect={false}
        dataTestId="usage-history-table"
        tableInnerContainerClassName="usage-history-table"
        infiniteScroll={false}
        enablePagination={true}
        rowSize="standard"
        offsetTableHeight={260}
        dropDownActionsArray={usageToolBarActions}
        filterBar={{
          filterBarRequired: true,
          filterConfig: usageFilters,
        }}
        loadDateFilter={loadDateFilter}
      />
    </>
  );
}

export default withRouter(UsageExplorer);
